import { z } from 'zod';

import { addressSchema } from '../address';
import {
  fulfillmentSchema,
  orderAdminSchema,
  orderCustomerSchema,
  orderOwnerSchema,
  requestedDeliveryTimeSchema,
} from '../order';
import { emailSchema, minorUnitsBigintSchema, phoneSchema, ulidSchema } from '../primitives';
import { reviewSelfSchema } from '../review';

import { cursorListResponseSchema } from './_helpers';

/** Cart + fulfillment slice shared by pricing preview and place order (excluding payment / guest). */
export const orderPricingCartBodySchema = z.object({
  restaurantId: ulidSchema,
  fulfillment: fulfillmentSchema,
  requestedDeliveryTime: requestedDeliveryTimeSchema,
  lineItems: z
    .array(
      z.object({
        menuItemId: ulidSchema,
        quantity: z.number().int().min(1).max(99),
      }),
    )
    .min(1),
  /** Reserved for vouchers/promotions; must align with place order when those ship. */
  voucherCodes: z.array(z.string().min(1).max(64)).max(20).optional(),
  promotionIds: z.array(ulidSchema).max(20).optional(),
});
export type OrderPricingCartBody = z.infer<typeof orderPricingCartBodySchema>;

export const orderPricingPreviewRequestSchema = orderPricingCartBodySchema;
export type OrderPricingPreviewRequest = z.infer<typeof orderPricingPreviewRequestSchema>;

export const orderPricingLineItemWireSchema = z.object({
  menuItemId: ulidSchema,
  name: z.string(),
  quantity: z.number().int(),
  unitPriceMinorUnits: z.string(),
  lineTotalMinorUnits: z.string(),
});

export const orderPricingPreviewResponseSchema = z.object({
  currency: z.string(),
  lineItems: z.array(orderPricingLineItemWireSchema),
  subtotalMinorUnits: z.string(),
  deliveryFeeMinorUnits: z.string(),
  deliveryFeeRecipient: z.enum(['restaurant', 'platform']),
  preDiscountTotalMinorUnits: z.string(),
  voucherDiscountMinorUnits: z.string(),
  voucherFromRestaurantPayoutMinorUnits: z.string(),
  voucherFromServiceFeeMinorUnits: z.string(),
  totalMinorUnits: z.string(),
  voucherId: ulidSchema.optional(),
});
export type OrderPricingPreviewResponse = z.infer<typeof orderPricingPreviewResponseSchema>;

export const placeOrderRequestSchema = orderPricingCartBodySchema.extend({
  clientExpectedTotalMinorUnits: minorUnitsBigintSchema,
  paymentProviderId: z.string().min(1),
  notes: z.string().max(2000).optional(),
  guest: z
    .object({
      email: emailSchema,
      phone: phoneSchema,
      displayName: z.string().min(1).max(140),
      defaultDeliveryAddress: addressSchema.optional(),
    })
    .optional(),
});
export type PlaceOrderRequest = z.infer<typeof placeOrderRequestSchema>;

export const placeOrderResponseSchema = z.object({
  orderId: ulidSchema,
  state: z.string(),
  totalMinorUnits: z.string(),
  /** When the chosen provider is hosted-redirect, this is where the customer must go. */
  checkoutUrl: z.string().url().optional(),
  /** Long-lived signed token allowing guest order tracking / dispute / review without auth. */
  guestOrderToken: z.string().optional(),
});
export type PlaceOrderResponse = z.infer<typeof placeOrderResponseSchema>;

export const customerDisputeRequestSchema = z.object({
  reason: z.string().min(10).max(1000),
  details: z.string().max(4000).optional(),
});
export type CustomerDisputeRequest = z.infer<typeof customerDisputeRequestSchema>;

export const restaurantDisputeResponseRequestSchema = z.discriminatedUnion('response', [
  z.object({
    response: z.literal('AGREE_FULL_REFUND'),
    restaurantNote: z.string().min(10).max(1000),
  }),
  z.object({
    response: z.literal('AGREE_PARTIAL_REFUND'),
    refundFromSubtotalMinorUnits: minorUnitsBigintSchema,
    refundFromDeliveryFeeMinorUnits: minorUnitsBigintSchema.default(0n),
    /** Restaurants are not allowed to use the platform-goodwill bucket. The handler enforces this. */
    refundFromPlatformGoodwillMinorUnits: minorUnitsBigintSchema.default(0n),
    restaurantNote: z.string().min(10).max(1000),
  }),
  z.object({
    response: z.literal('DECLINE'),
    restaurantNote: z.string().min(10).max(1000),
  }),
]);
export type RestaurantDisputeResponseRequest = z.infer<
  typeof restaurantDisputeResponseRequestSchema
>;

export const adminDisputeResolveRequestSchema = z.discriminatedUnion('resolution', [
  z.object({
    resolution: z.literal('PAYOUT'),
    adminNote: z.string().min(10).max(1000),
  }),
  z.object({
    resolution: z.literal('FULL_REFUND'),
    adminNote: z.string().min(10).max(1000),
  }),
  z.object({
    resolution: z.literal('PARTIAL_REFUND'),
    refundFromSubtotalMinorUnits: minorUnitsBigintSchema.default(0n),
    refundFromDeliveryFeeMinorUnits: minorUnitsBigintSchema.default(0n),
    refundFromPlatformGoodwillMinorUnits: minorUnitsBigintSchema.default(0n),
    adminNote: z.string().min(10).max(1000),
  }),
]);
export type AdminDisputeResolveRequest = z.infer<typeof adminDisputeResolveRequestSchema>;

export const orderOwnerListResponseSchema = cursorListResponseSchema(orderOwnerSchema);
export type OrderOwnerListResponse = z.infer<typeof orderOwnerListResponseSchema>;

export const orderAdminListResponseSchema = cursorListResponseSchema(orderAdminSchema);
export type OrderAdminListResponse = z.infer<typeof orderAdminListResponseSchema>;

export const orderReviewSelfResponseSchema = z.object({
  review: reviewSelfSchema.nullable(),
});
export type OrderReviewSelfResponse = z.infer<typeof orderReviewSelfResponseSchema>;

export const orderCustomerPathParamsSchema = z.object({
  id: ulidSchema,
});
export type OrderCustomerPathParams = z.infer<typeof orderCustomerPathParamsSchema>;

export const orderCustomerResponseSchema = orderCustomerSchema;

export const orderCustomerListResponseSchema = cursorListResponseSchema(orderCustomerSchema);
export type OrderCustomerListResponse = z.infer<typeof orderCustomerListResponseSchema>;

export const restaurantAndOrderPathParamsSchema = z.object({
  id: ulidSchema,
  orderId: ulidSchema,
});
export type RestaurantAndOrderPathParams = z.infer<typeof restaurantAndOrderPathParamsSchema>;

export const restaurantOrderAdvanceStepSchema = z.enum([
  'PREPARING',
  'READY',
  'OUT_FOR_DELIVERY',
  'COMPLETE',
]);
export type RestaurantOrderAdvanceStep = z.infer<typeof restaurantOrderAdvanceStepSchema>;

export const restaurantOrderAdvanceRequestSchema = z.object({
  step: restaurantOrderAdvanceStepSchema,
});
export type RestaurantOrderAdvanceRequest = z.infer<typeof restaurantOrderAdvanceRequestSchema>;

export const restaurantOwnerOrdersWireQuerySchema = z
  .object({
    limit: z.coerce.number().int().optional(),
    cursor: z.string().optional(),
  })
  .transform((value) => ({
    limit:
      value.limit !== undefined &&
      Number.isFinite(value.limit) &&
      value.limit > 0 &&
      value.limit <= 100
        ? value.limit
        : 25,
    ...(value.cursor !== undefined && value.cursor.trim().length > 0
      ? { cursor: value.cursor.trim() }
      : {}),
  }));

export type RestaurantOwnerOrdersWireQuery = z.infer<typeof restaurantOwnerOrdersWireQuerySchema>;

export const customerOrdersWireQuerySchema = restaurantOwnerOrdersWireQuerySchema;
export type CustomerOrdersWireQuery = z.infer<typeof customerOrdersWireQuerySchema>;
